// src/utils/RemoteConfig.js
import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';
import app from '../firebase/firebase';

// Initialize Remote Config
const remoteConfig = getRemoteConfig(app);

// Set default values (used if Remote Config fetch fails)
remoteConfig.defaultConfig = {
  payment_enforcement_enabled: false,
};

// Configure settings
remoteConfig.settings = {
  minimumFetchIntervalMillis: 3600000, // 1 hour (can be lower in dev: 60000 = 1 minute)
  fetchTimeoutMillis: 10000, // 10 seconds
};

/**
 * Initialize and fetch Remote Config values
 * Call this once when app loads
 */
export const initializeRemoteConfig = async () => {
  try {
    console.log('RemoteConfig - Fetching configuration...');
    const activated = await fetchAndActivate(remoteConfig);
    
    if (activated) {
      console.log('RemoteConfig - New config activated');
    } else {
      console.log('RemoteConfig - Using cached config');
    }
    
    // Log current values for debugging
    const paymentEnabled = getValue(remoteConfig, 'payment_enforcement_enabled').asBoolean();
    console.log('RemoteConfig - payment_enforcement_enabled:', paymentEnabled);
    
    return true;
  } catch (error) {
    console.error('RemoteConfig - Error fetching config:', error);
    // Use defaults on error
    return false;
  }
};

/**
 * Check if payment enforcement is enabled
 * Supports localStorage override for local testing:
 * - Set localStorage.setItem('DEBUG_PAYMENT_ENFORCEMENT', 'true') to force enable
 * - Set localStorage.setItem('DEBUG_PAYMENT_ENFORCEMENT', 'false') to force disable
 * - Remove localStorage.removeItem('DEBUG_PAYMENT_ENFORCEMENT') to use Remote Config
 * 
 * @returns {boolean} - True if users need subscriptions, false if beta mode
 */
export const isPaymentEnforcementEnabled = () => {
  try {
    // Check for local testing override first
    const debugOverride = localStorage.getItem('DEBUG_PAYMENT_ENFORCEMENT');
    if (debugOverride !== null) {
      const overrideValue = debugOverride === 'true';
      console.log('RemoteConfig - Using DEBUG override:', overrideValue);
      return overrideValue;
    }
    
    // Otherwise use Remote Config
    const value = getValue(remoteConfig, 'payment_enforcement_enabled').asBoolean();
    console.log('RemoteConfig - Using Remote Config value:', value);
    return value;
  } catch (error) {
    console.error('RemoteConfig - Error getting payment_enforcement_enabled:', error);
    // Fail safe: default to false (don't lock users out)
    return false;
  }
};

/**
 * Force refresh Remote Config (useful for testing)
 */
export const refreshRemoteConfig = async () => {
  try {
    console.log('RemoteConfig - Force refresh requested');
    // Set minimal fetch interval for immediate refresh
    remoteConfig.settings.minimumFetchIntervalMillis = 0;
    await fetchAndActivate(remoteConfig);
    // Reset to normal interval
    remoteConfig.settings.minimumFetchIntervalMillis = 3600000;
    console.log('RemoteConfig - Refresh complete');
  } catch (error) {
    console.error('RemoteConfig - Error refreshing config:', error);
  }
};

export default remoteConfig;